// Maps Laravel-style 422 validation errors ({ backendKey: [messages] })
// to frontend form field keys so the fields can be highlighted.
//
// Backend keys often differ slightly from frontend keys
// (e.g. "networkStorageId" -> "networkStorage"). The heuristic tries:
// 1. an explicit mapping via `keyMap` ({ backendKey: frontendKey })
// 2. an exact (case-insensitive) match
// 3. the key with a trailing "Id"/"Ids" stripped (+ simple plural)
//
// Returns:
// - fieldErrors: { frontendKey: message } for highlighting via FormFields
// - messages: [{ key, label, message }] every message, with the matched
//   field label when the key could be resolved (for a summary display)
export default function mapServerErrors(serverErrors, fields, keyMap = {}) {
  const fieldErrors = {};
  const messages = [];
  const fieldKeys = (fields || []).map((f) => f.key).filter(Boolean);

  const findFieldKey = (candidate) =>
    fieldKeys.find((fk) => fk.toLowerCase() === candidate.toLowerCase());

  const resolveKey = (backendKey) => {
    if (keyMap[backendKey]) return keyMap[backendKey];

    const candidates = [backendKey];
    if (/Ids?$/.test(backendKey)) {
      const stripped = backendKey.replace(/Ids?$/, '');
      candidates.push(stripped, `${stripped}s`);
    }

    for (const candidate of candidates) {
      const match = findFieldKey(candidate);
      if (match) return match;
    }
    return null;
  };

  Object.entries(serverErrors || {}).forEach(([backendKey, msgs]) => {
    const msgArr = Array.isArray(msgs) ? msgs : [msgs];
    const frontendKey = resolveKey(backendKey);
    const field = (fields || []).find((f) => f.key === frontendKey);

    if (frontendKey && msgArr.length) fieldErrors[frontendKey] = msgArr[0];

    msgArr.forEach((message) =>
      messages.push({ key: backendKey, label: field?.label, message })
    );
  });

  return { fieldErrors, messages };
}
